import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { axiosInstance } from "../App";
import { ArrowLeft, Loader2, Mail, Save } from "lucide-react";
import { toast } from "sonner";

function SMTPSettings() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [companyName, setCompanyName] = useState("");
  const [testEmailAddress, setTestEmailAddress] = useState("");
  const [sendingTestEmail, setSendingTestEmail] = useState(false);
  const [smtpForm, setSmtpForm] = useState({
    mail_server: "",
    mail_port: 587,
    mail_username: "",
    mail_password: "",
    mail_from: "",
    mail_from_name: "",
    mail_starttls: true,
    mail_ssl_tls: false
  });

  useEffect(() => {
    // Check if user is admin
    const userData = JSON.parse(localStorage.getItem("user") || "{}");
    if (userData.role !== "admin") {
      toast.error(t('accessDenied'));
      navigate("/dashboard");
      return;
    }
    fetchSettings();
  }, [navigate]);

  const fetchSettings = async () => {
    try {
      const response = await axiosInstance.get("/admin/smtp-settings");
      setCompanyName(response.data.company_name);
      if (response.data.smtp_settings) {
        setSmtpForm({
          mail_server: response.data.smtp_settings.mail_server || "",
          mail_port: response.data.smtp_settings.mail_port || 587,
          mail_username: response.data.smtp_settings.mail_username || "",
          mail_password: response.data.smtp_settings.mail_password || "",
          mail_from: response.data.smtp_settings.mail_from || "",
          mail_from_name: response.data.smtp_settings.mail_from_name || "",
          mail_starttls: response.data.smtp_settings.mail_starttls !== false,
          mail_ssl_tls: response.data.smtp_settings.mail_ssl_tls === true
        });
      }
    } catch (error) {
      toast.error("Ayarlar yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axiosInstance.put("/admin/smtp-settings", smtpForm);
      toast.success(t('smtpSaved'));
      fetchSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('smtpSaveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleTestEmail = async () => {
    if (!testEmailAddress) {
      toast.error("Lütfen e-posta adresi girin");
      return;
    }

    if (!smtpForm.mail_server) {
      toast.error("Lütfen önce SMTP ayarlarını doldurun");
      return;
    }

    setSendingTestEmail(true);
    try {
      const response = await axiosInstance.post("/admin/smtp/test-email", null, {
        params: { test_email: testEmailAddress }
      });
      toast.success(response.data.message || "Test e-postası başarıyla gönderildi!");
      setTestEmailAddress("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Test e-postası gönderilemedi");
    } finally {
      setSendingTestEmail(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: '#F8F9FA' }}>
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <img 
                src="/milestora-logo-light.png" 
                alt="Milestora" 
                className="h-20 w-auto object-contain"
              />
              <div>
                <h1 className="text-2xl font-bold" style={{ fontFamily: 'Manrope', color: '#1F2937' }}>
                  {t('smtpSettings')}
                </h1>
                <p className="text-sm text-gray-500 mt-1">{companyName} - {t('emailServerConfig')}</p>
              </div>
            </div>
            <Button variant="outline" onClick={() => navigate("/dashboard")}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              {t('backToDashboard')}
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-blue-500" />
              <div>
                <CardTitle>{t('emailServerSettings')}</CardTitle>
                <CardDescription className="mt-1">
                  {t('configureSMTP')}
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSave} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="mail_server">{t('smtpServer')} *</Label>
                  <Input
                    id="mail_server"
                    value={smtpForm.mail_server}
                    onChange={(e) => setSmtpForm({ ...smtpForm, mail_server: e.target.value })}
                    placeholder="smtp.gmail.com"
                    required
                  />
                  <p className="text-xs text-gray-500">{t('exampleServers')}</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="mail_port">{t('smtpPort')} *</Label>
                  <Input
                    id="mail_port"
                    type="number"
                    value={smtpForm.mail_port}
                    onChange={(e) => setSmtpForm({ ...smtpForm, mail_port: parseInt(e.target.value) })}
                    placeholder="587"
                    required
                  />
                  <p className="text-xs text-gray-500">587 (STARTTLS) / 465 (SSL)</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="mail_username">{t('usernameEmail')} *</Label>
                  <Input
                    id="mail_username"
                    type="email"
                    value={smtpForm.mail_username}
                    onChange={(e) => setSmtpForm({ ...smtpForm, mail_username: e.target.value })}
                    placeholder="noreply@company.com"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="mail_password">{t('password')} *</Label>
                  <Input
                    id="mail_password"
                    type="password"
                    value={smtpForm.mail_password}
                    onChange={(e) => setSmtpForm({ ...smtpForm, mail_password: e.target.value })}
                    placeholder="••••••••"
                    required
                  />
                  <p className="text-xs text-gray-500">{t('useAppPasswordGmail')}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="mail_from">{t('senderEmail')}</Label>
                  <Input
                    id="mail_from"
                    type="email"
                    value={smtpForm.mail_from}
                    onChange={(e) => setSmtpForm({ ...smtpForm, mail_from: e.target.value })}
                    placeholder="notification@company.com"
                  />
                  <p className="text-xs text-gray-500">{t('ifEmptyUsername')}</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="mail_from_name">{t('senderName')}</Label>
                  <Input
                    id="mail_from_name"
                    value={smtpForm.mail_from_name}
                    onChange={(e) => setSmtpForm({ ...smtpForm, mail_from_name: e.target.value })}
                    placeholder={t('companyName')}
                  />
                  <p className="text-xs text-gray-500">{t('nameInEmails')}</p>
                </div>
              </div>

              <div className="space-y-4 pt-4 border-t">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="mail_starttls">{t('useSTARTTLS')}</Label>
                    <p className="text-xs text-gray-500">{t('recommendedPort587')}</p>
                  </div>
                  <Switch
                    id="mail_starttls"
                    checked={smtpForm.mail_starttls}
                    onCheckedChange={(checked) => setSmtpForm({ ...smtpForm, mail_starttls: checked })}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="mail_ssl_tls">{t('useSSLTLS')}</Label>
                    <p className="text-xs text-gray-500">{t('recommendedPort465')}</p>
                  </div>
                  <Switch
                    id="mail_ssl_tls"
                    checked={smtpForm.mail_ssl_tls}
                    onCheckedChange={(checked) => setSmtpForm({ ...smtpForm, mail_ssl_tls: checked })}
                  />
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-blue-900 mb-2">📧 {t('gmailImportantNote')}</h4>
                <p className="text-xs text-blue-800">
                  {t('gmailAppPassword')}
                </p>
              </div>

              {/* Test Email Section */}
              <div className="border-t pt-6 space-y-4">
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 mb-1">Test E-postası Gönder</h4>
                  <p className="text-xs text-gray-500">Ayarları kaydetmeden önce test edebilirsiniz</p>
                </div>
                <div className="flex gap-2">
                  <Input
                    type="email"
                    value={testEmailAddress}
                    onChange={(e) => setTestEmailAddress(e.target.value)}
                    placeholder="test@example.com"
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleTestEmail}
                    disabled={sendingTestEmail || !smtpForm.mail_server}
                    className="whitespace-nowrap"
                  >
                    {sendingTestEmail ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Gönderiliyor
                      </>
                    ) : (
                      <>
                        <Mail className="h-4 w-4 mr-2" />
                        Test Gönder
                      </>
                    )}
                  </Button>
                </div>
              </div>

              <Button type="submit" className="w-full" disabled={saving}>
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {t('saving')}
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    {t('saveSettings')}
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}

export default SMTPSettings;
